import React, { useState, useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import api from "../services/api";
import UsersTab from "./admin/UsersTab";
import AgentsTab from "./admin/AgentsTab";
import SessionsTab from "./admin/SessionsTab";
import ExecutionsTab from "./admin/ExecutionsTab";
import ProjectsTab from "./admin/ProjectsTab";
import AccessLinksTab from "./admin/AccessLinksTab";

const NAV_ITEMS_ADMIN = [
  { key: "users", label: "Users", icon: UsersIcon },
  { key: "access-links", label: "Access Links", icon: LinkIcon },
  { key: "agents", label: "Agents", icon: AgentsIcon },
  { key: "sessions", label: "Sessions", icon: SessionsIcon },
  { key: "executions", label: "Executions", icon: ExecutionsIcon },
  { key: "projects", label: "Projects", icon: ProjectsIcon },
];

// Access links are the one thing a subadmin can hand out on their own, so the
// tab sits with the two they already have rather than behind an admin gate.
const NAV_ITEMS_SUBADMIN = [
  { key: "users", label: "Users", icon: UsersIcon },
  { key: "access-links", label: "Access Links", icon: LinkIcon },
  { key: "sessions", label: "Sessions", icon: SessionsIcon },
];

export default function AdminPanel({ currentUser, onBackToApp }) {
  const isAdmin = currentUser?.role === "admin";
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Derive active tab from URL: /admin/users → "users"
  const activeTab = location.pathname.split("/")[2] || "users";

  const navItems = isAdmin ? NAV_ITEMS_ADMIN : NAV_ITEMS_SUBADMIN;

  useEffect(() => {
    api
      .adminGetStats()
      .then((r) => setStats(r.data.stats))
      .catch(() => {});
  }, []);

  const goTo = (key) => {
    navigate(`/admin/${key}`);
    setMobileNavOpen(false);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--bg)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top bar */}
      <div
        style={{
          borderBottom: "1px solid var(--border)",
          background: "var(--surface)",
          padding: "0 20px",
          height: "56px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileNavOpen((v) => !v)}
            className="admin-mobile-ham"
            style={{
              display: "none",
              background: "none",
              border: "none",
              color: "var(--text-secondary)",
              cursor: "pointer",
              padding: "4px",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <rect y="3" width="20" height="2" rx="1" />
              <rect y="9" width="20" height="2" rx="1" />
              <rect y="15" width="20" height="2" rx="1" />
            </svg>
          </button>
          <span
            style={{
              fontSize: "13px",
              fontWeight: "700",
              color: "var(--text-primary)",
            }}
          >
            {isAdmin ? "Admin Panel" : "Subadmin Panel"}
          </span>
          <span
            style={{
              fontSize: "10px",
              padding: "2px 8px",
              borderRadius: "5px",
              background: isAdmin
                ? "rgba(245,158,11,0.12)"
                : "rgba(167,139,250,0.12)",
              border: `1px solid ${isAdmin ? "rgba(245,158,11,0.3)" : "rgba(167,139,250,0.3)"}`,
              color: isAdmin ? "#f59e0b" : "#a78bfa",
              fontWeight: "700",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            {currentUser?.role}
          </span>
          <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>
            {currentUser?.fullName || currentUser?.username}
          </span>
        </div>
        <button
          onClick={onBackToApp}
          style={{
            fontSize: "12px",
            padding: "6px 14px",
            background: "rgba(255,255,255,0.05)",
            border: "1px solid var(--border)",
            borderRadius: "8px",
            color: "var(--text-muted)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            whiteSpace: "nowrap",
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M7.5 2L3.5 6L7.5 10"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Back to App
        </button>
      </div>

      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Sidebar nav */}
        <nav
          style={{
            width: "200px",
            borderRight: "1px solid var(--border)",
            background: "var(--surface)",
            padding: "16px 10px",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          {stats && (
            <div
              style={{
                marginBottom: "16px",
                padding: "12px",
                background: "rgba(255,255,255,0.03)",
                border: "1px solid var(--border)",
                borderRadius: "10px",
              }}
            >
              <StatRow label="Users" value={stats.totalUsers} />
              {isAdmin && <StatRow label="Agents" value={stats.totalAgents} />}
              <StatRow label="Active Sessions" value={stats.activeSessions} />
              {isAdmin && (
                <StatRow label="Executions" value={stats.totalExecutions} last />
              )}
            </div>
          )}
          {navItems.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => goTo(key)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "9px",
                padding: "9px 12px",
                borderRadius: "9px",
                border: "none",
                background:
                  activeTab === key ? "rgba(6,182,212,0.12)" : "transparent",
                color:
                  activeTab === key ? "var(--teal-bright)" : "var(--text-muted)",
                fontSize: "13px",
                fontWeight: activeTab === key ? "600" : "500",
                cursor: "pointer",
                textAlign: "left",
                width: "100%",
                transition: "all 0.12s",
              }}
            >
              <Icon active={activeTab === key} />
              {label}
            </button>
          ))}
        </nav>

        {/* Mobile nav overlay */}
        {mobileNavOpen && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 500,
              background: "rgba(0,0,0,0.6)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
            }}
            onClick={() => setMobileNavOpen(false)}
          >
            <nav
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "220px",
                background: "var(--surface)",
                borderRight: "1px solid var(--border)",
                padding: "16px 10px",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
              }}
            >
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "var(--text-primary)",
                  padding: "8px 12px 14px",
                }}
              >
                {isAdmin ? "Admin Panel" : "Subadmin Panel"}
              </div>
              {navItems.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => goTo(key)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "9px",
                    padding: "9px 12px",
                    borderRadius: "9px",
                    border: "none",
                    background:
                      activeTab === key
                        ? "rgba(6,182,212,0.12)"
                        : "transparent",
                    color:
                      activeTab === key
                        ? "var(--teal-bright)"
                        : "var(--text-muted)",
                    fontSize: "13px",
                    fontWeight: activeTab === key ? "600" : "500",
                    cursor: "pointer",
                    textAlign: "left",
                    width: "100%",
                    transition: "all 0.12s",
                  }}
                >
                  <Icon active={activeTab === key} />
                  {label}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* Main content — routed */}
        <main style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
          <Routes>
            {/* /admin → /admin/users */}
            <Route index element={<Navigate to="users" replace />} />

            <Route path="users" element={<UsersTab currentUser={currentUser} />} />

            <Route
              path="agents"
              element={
                isAdmin ? (
                  <AgentsTab />
                ) : (
                  <Navigate to="/admin/users" replace />
                )
              }
            />

            <Route
              path="access-links"
              element={<AccessLinksTab currentUser={currentUser} />}
            />

            <Route
              path="sessions"
              element={<SessionsTab currentUser={currentUser} />}
            />

            <Route
              path="executions"
              element={
                isAdmin ? (
                  <ExecutionsTab currentUser={currentUser} />
                ) : (
                  <Navigate to="/admin/users" replace />
                )
              }
            />

            <Route
              path="projects"
              element={
                isAdmin ? (
                  <ProjectsTab />
                ) : (
                  <Navigate to="/admin/users" replace />
                )
              }
            />

            {/* Any unrecognised /admin/* path */}
            <Route path="*" element={<Navigate to="users" replace />} />
          </Routes>
        </main>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .admin-mobile-ham { display: flex !important; }
          nav[style*="width: 200px"] { display: none !important; }
        }
      `}</style>
    </div>
  );
}

function StatRow({ label, value, last }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: last ? 0 : "6px",
      }}
    >
      <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>
        {label}
      </span>
      <span
        style={{ fontSize: "12px", fontWeight: "700", color: "var(--text-secondary)" }}
      >
        {value}
      </span>
    </div>
  );
}

function UsersIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function AgentsIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="2" width="5" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
      <rect x="9" y="2" width="5" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
      <rect x="2" y="9" width="5" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
      <rect x="9" y="9" width="5" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

function SessionsIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4" />
      <polyline points="8,5 8,8.5 10.5,10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ExecutionsIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path d="M6 3L12 8L6 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="2" y1="8" x2="6" y2="8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path
        d="M6.5 9.5a2.8 2.8 0 004 0l2-2a2.83 2.83 0 00-4-4l-.6.6"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <path
        d="M9.5 6.5a2.8 2.8 0 00-4 0l-2 2a2.83 2.83 0 004 4l.6-.6"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ProjectsIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path
        d="M1.5 3.5A1 1 0 012.5 2.5h4l1.5 2H13.5a1 1 0 011 1v7a1 1 0 01-1 1h-11a1 1 0 01-1-1v-9z"
        stroke="currentColor"
        strokeWidth="1.4"
        fill="none"
      />
    </svg>
  );
}
